import { uniqueId } from 'lodash';
import moment from 'moment';
import { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getDashboardData } from '../../../api/dashboard';
import { pastDuration, reportTypes } from '../../../constants/reports';
import { routes } from '../../../constants/routes';
import { dateFormat, fetchDashboardFilterData } from '../../../utils/constant';
import { apiErrorHandler, getStartDateEndDate } from '../../../utils/handlers';
import DashboardRecentContracts from '../DashboardRecentContracts';
import ARSegmentBreakdown from '../graph-cards/AR-seg-breakdown';
import BilledVsPaidCard from '../graph-cards/billed-vs-paid';
import IndividualDashboardLayout from '../IndividualDashboardLayout';
import CollectionsCard from '../overview-cards/collections-card';
import {
  COLLECTIONS_STRUCTURE,
  DASHBOARD_COLORS,
  DURATION,
  VALIDATED_CONTRACTS_HEADERS,
} from '../utils/dashboardConstants';

function CollectionsDashboard() {
  const history = useHistory();
  const [date, setDate] = useState(getStartDateEndDate(pastDuration[2].value));
  const [duration, setDuration] = useState(pastDuration[2]);
  const [contractNumber, setContractNumber] = useState('');
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const filtersReady = filterData?.entity?.length > 0;
  const segmentPieId = useMemo(() => uniqueId('collections-segmentation-pie-id'), []);

  const [collectionLoading, setCollectionLoading] = useState(false);
  const [bvpLoading, setBVPLoading] = useState(false);
  const [subContractLoading, setSubContractLoading] = useState(false);
  const [collectionData, setCollectionData] = useState({
    ...COLLECTIONS_STRUCTURE,
  });
  const [bvpData, setBVPData] = useState({
    ...COLLECTIONS_STRUCTURE,
  });
  const [subContractData, setSubContractData] = useState({
    ...COLLECTIONS_STRUCTURE,
  });

  const getDashboardCollections = async () => {
    setCollectionLoading(true);
    try {
      const dataAvailable = fetchDashboardFilterData(filterData);
      const reqParams = {
        ...dataAvailable,
        opiOrName: contractNumber,
        startDate: moment(date.startDate).format(dateFormat.YEAR_MONTH_DAY),
        endDate: moment(date.endDate).format(dateFormat.YEAR_MONTH_DAY),
        timezone: moment.tz.guess(),
      };
      const { data: responseData } = await getDashboardData('dashboard', 'collections', reqParams);
      setCollectionData(responseData.data.items);
    } catch (error) {
      setCollectionLoading(false);
      apiErrorHandler(error);
    } finally {
      setCollectionLoading(false);
    }
  };
  const getDashboardBVP = async () => {
    setBVPLoading(true);
    try {
      const dataAvailable = fetchDashboardFilterData(filterData);
      const reqParams = {
        ...dataAvailable,
        opiOrName: contractNumber,
        startDate: moment(date.startDate).format(dateFormat.YEAR_MONTH_DAY),
        endDate: moment(date.endDate).format(dateFormat.YEAR_MONTH_DAY),
        timezone: moment.tz.guess(),
      };
      const { data: responseData } = await getDashboardData('dashboard', 'billedVsPaid', reqParams);
      setBVPData(responseData.data.items);
    } catch (error) {
      if (error.entityError) error.response = null;
      setBVPLoading(false);
      apiErrorHandler(error);
    } finally {
      setBVPLoading(false);
    }
  };
  const getDashboardSubmittedContracts = async () => {
    setSubContractLoading(true);
    try {
      const dataAvailable = fetchDashboardFilterData(filterData);
      const reqParams = {
        ...dataAvailable,
        opiOrName: contractNumber,
        startDate: moment(date.startDate).format(dateFormat.YEAR_MONTH_DAY),
        endDate: moment(date.endDate).format(dateFormat.YEAR_MONTH_DAY),
        timezone: moment.tz.guess(),
      };
      const { data: responseData } = await getDashboardData(
        'dashboard',
        'submittedContracts',
        reqParams,
      );
      setSubContractData(responseData.data.items);
    } catch (error) {
      if (error.entityError) error.response = null;
      setSubContractLoading(false);
      apiErrorHandler(error);
    } finally {
      setSubContractLoading(false);
    }
  };

  useEffect(() => {
    if (filtersReady) {
      getDashboardCollections();
      getDashboardSubmittedContracts();
      getDashboardBVP();
    }
  }, [filtersReady, filterData, date, contractNumber]);

  const redirectionHandler = (query) => {
    const updatedQuery = { ...query };
    updatedQuery.reportDate = date.endDate;
    updatedQuery.startDate = date.startDate;
    updatedQuery.endDate = date.endDate;
    updatedQuery.duration = duration;
    history.push({ pathname: query.pathname, state: updatedQuery });
  };

  const dateChangeHandler = (changedDate, changedDuration) => {
    setDate(changedDate);
    setDuration(changedDuration);
  };
  const searchHandler = (searchedValue) => setContractNumber(searchedValue);

  const getReportTypeValues = (reportType) => {
    return reportTypes.find((report) => report.value === reportType);
  };

  return (
    <IndividualDashboardLayout
      durationType={DURATION.PAST}
      dateChangeHandler={dateChangeHandler}
      searchHandler={searchHandler}
      className="collection-dashboard"
    >
      <div className="dashboard-row-container">
        <CollectionsCard
          data={{
            amount: collectionData?.collections?.totalAmount,
            percent: collectionData?.collections?.percentChange,
            date: collectionData?.collections?.lastUpdatedAt || new Date(),
            data: collectionData?.collections?.data || [],
            redirection: {
              enabled: false,
              path: routes.REPORTS,
              filterType: 'reportType',
              redirect: redirectionHandler,
              values: getReportTypeValues('arAdjustmentReport'),
            },
          }}
          bgColor={DASHBOARD_COLORS.COLLECTIONS}
          loader={collectionLoading}
        />
        <ARSegmentBreakdown
          id={segmentPieId}
          loader={collectionLoading}
          data={{
            isCollections: true,
            graphType: 'pie',
            title: 'Collection (Segment Breakdown)',
            tooltip:
              'This chart will show the amount of total collections in business unit segment breakdown.',
            segmentation: (collectionData?.collections?.segmentation || []).map((mode) => {
              return {
                serviceType: mode.label,
                totalAmount: mode.amount,
              };
            }),
            totalAmount: collectionData?.collections?.totalAmount,
            redirection: {
              enabled: false,
            },
          }}
        />
      </div>
      <BilledVsPaidCard
        title="Billed vs Paid"
        tooltip={{
          cardTooltip:
            'This chart will show the total amount of contract submitted & payment received',
          totalCashflowTooltip: 'This represents the total amount of money received',
        }}
        loader={bvpLoading}
        totalCashflowTooltip
        data={{
          ...bvpData.billedVsPaid,
          redirection: {
            enabled: false,
            path: routes.REPORTS,
            filterType: 'reportType',
            redirect: redirectionHandler,
            values: getReportTypeValues('arAdjustmentReport'),
          },
          lastUpdatedAt: bvpData.lastUpdatedAt,
        }}
      />
      <DashboardRecentContracts
        headers={VALIDATED_CONTRACTS_HEADERS}
        data={subContractData.validatedContracts}
        loader={subContractLoading}
        duration={{
          duration,
          range: date,
        }}
      />
    </IndividualDashboardLayout>
  );
}

export default memo(CollectionsDashboard);
